import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import {
  MuiButton,
  MuiContainer,
  MuiInputAdornment,
  MuiMenu,
  MuiMenuItem,
  MuiTextField,
} from '@/features/shared/material-ui';
import { Theme } from '@/utils/constants';
import { formatText } from '@/utils/helpers';
import { Search as SearchIcon } from '@material-ui/icons';
import clsx from 'clsx';
import React, {
  ChangeEvent,
  KeyboardEvent,
  memo,
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchBarStyles, useStyles } from './SearchBar.styles';

const SearchBar = ({
  size = 'normal',
  displayInHero = false,
  labels,
  theme = Theme.light,
}: any) => {
  const classes = useStyles({});
  const searchBarClasses = useSearchBarStyles({});
  const { queryParams, updateQueryParams } = useContext(ElasticComponentContext);
  const [input, setInput] = useState(queryParams.q);
  const [searchBy, setSearchBy] = useState(queryParams.searchBy);
  const [searchInLabel, setSearchInLabel] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const variantName = (containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
    ?.dataset?.datalayerVariant;
  const isRevision = useIsRevision();

  useEffect(() => {
    setInput(queryParams.q || '');
  }, [queryParams.q]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = (engine: string) => {
    updateQueryParams('q', input);
    updateQueryParams('page', 1);
    updateQueryParams('searchBy', engine);
    setSearchBy(engine);
    handleClose();

    datalayerPush({
      compRef: containerRef,
      args: {
        event: 'searchQuery',
        revision: isRevision,
        query: input,
        filters: { searchBy: engine },
        variantName,
      },
      event: 'gtm:custom:search',
    });
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handlePress = (event: KeyboardEvent<HTMLInputElement>) => {
    const isEnter = /^enter$/i.test(event.key);

    if (isEnter) {
      updateQueryParams('q', input);
      updateQueryParams('page', 1);

      datalayerPush({
        compRef: containerRef,
        args: {
          event: 'searchQuery',
          revision: isRevision,
          query: input,
          variantName,
        },
        event: 'gtm:custom:search',
      });
    }
  };
  const isLargeDesktop = useMemo(() => /large/.test(size), [size]);
  const rootSearchBarClasses = clsx(
    classes.wrapper,
    `${theme}-theme`,
    isLargeDesktop && 'desktop-large',
  );

  useMemo(() => {
    setSearchInLabel(formatText(labels?.search_in || 'In %1', [searchBy]));
  }, [labels?.search_in, searchBy]);

  const component = (
    <div className={rootSearchBarClasses} ref={containerRef}>
      <div style={{ flexGrow: 1 }}>
        <MuiTextField
          fullWidth
          onChange={handleInput}
          onKeyPress={handlePress}
          value={input || ''}
          InputProps={{
            classes: searchBarClasses,
            placeholder: labels?.search || 'Search the marketplace',
            startAdornment: (
              <MuiInputAdornment position="start" className={classes.icon}>
                <SearchIcon fontSize={/large/.test(size) ? 'default' : 'default'} />
              </MuiInputAdornment>
            ),
            disableUnderline: true,
          }}
        />
      </div>
      <MuiButton
        className={classes.button}
        variant="contained"
        color="secondary"
        onClick={handleClick}
      >
        <span className={classes.mobile}>{searchInLabel}</span> +
      </MuiButton>
      <MuiMenu
        id="simple-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MuiMenuItem onClick={() => handleApply('solutions')}>
          {labels?.search_solutions || 'Solutions'}
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleApply('partners')}>
          {labels?.search_partners || 'Partners'}
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleApply('both')}>
          {labels?.search_both || 'Both'}
        </MuiMenuItem>
      </MuiMenu>
    </div>
  );

  return displayInHero ? (
    <MuiContainer className={clsx(classes.container, 'medium')}>{component}</MuiContainer>
  ) : (
    component
  );
};

export default memo(SearchBar);
